import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { remove as _remove } from 'lodash';
import { Stage, Layer, Rect, Text, Group, Path, Line } from 'react-konva';

import DefaultMineIcon from '../svg/DefaultMineIcon';
import { colorToArray, isColorBright, RGB_2_HEX } from '../../lib/ep';

const MineSweeperCanvas = forwardRef(({ stage, ...props }, ref) => {
	const [ full_loaded, setFullLoaded ] = useState(false);
	
	const setGrid = () => {
		const grid = stage?.minesweeper_grid ?? 'small';

		if(grid == 'large') return 14;
		if(grid == 'medium') return 10;
		return 6;
	};

	const refs = useRef([]);
	const size = useRef(600);
	const flag_event = useRef(false);
	const columns = useRef(setGrid());
	const map = useRef([]);
	const areas = useRef([]);
	const buttonPressTimer = useRef();
	const ended = useRef(false);
	const score = useRef(0);
	const is_playable = useRef(false);
	const is_mounting = useRef(false);

	const cell_size = size.current / columns.current;
	const delay_to_success = 2; // in seconds

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;
	const hack_enabled = (is_builder && is_test) ? (props?.with_hack ?? true) : false;

	const bomb_char_id = "X";
	const clear_char_id = "";

	const total_bombs = parseInt(stage?.minesweeper_mines ?? 10);
	const total_cells = Math.pow(columns.current, 2);

	const bombs_in_mount_animation = Math.min(total_bombs, 8);
	const angle_between_bombs = 360 / bombs_in_mount_animation;
	const circle_radius = size.current / 5;

	const flag_bg_color = stage?.minesweeper_flag_color ?? 'rgba(120, 195, 0, 1)';
	const exploded_bomb_bg_color = stage?.minesweeper_bomb_color ?? 'rgba(215, 35, 60, 1)';
	const board_bg_color = stage?.bg_color ?? 'rgba(255, 255, 255, 1)';
	const cell_bg_color = `rgba(${colorToArray(stage?.minesweeper_cell_color ?? 'rgba(255, 255, 255, 1)')}, 1)`;
	const grid_bg_color = stage?.minesweeper_grid_color ?? 'rgba(0, 0, 0, 1)';

	const is_bg_color_bright = isColorBright(RGB_2_HEX(cell_bg_color));
	const is_flag_color_bright = isColorBright(RGB_2_HEX(flag_bg_color));
	const text_color = stage?.text_color ?? 'rgba(0, 0, 0, 1)';

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	/*START SOUNDS*/
	const click_cell_sound = props.sound_fx?.flip;
	const flagCellPlay = () => props?.sound_fx?.flag.play();
	const unflagCellPlay = () => props?.sound_fx?.unplug.play();
	const bombExplodePlay = () => props?.sound_fx?.error.play();
	const clickCellPlay = () => click_cell_sound.play();
	const successPlay = () => props?.sound_fx?.success.play();
	const failPlay = () => props?.sound_fx?.fail.play();

	const clickAndExplodeBombPlay = () => {
		click_cell_sound.once('end', () => {
			bombExplodePlay();
		});
		clickCellPlay();
	};
	/*END SOUNDS*/

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		if(is_builder) return;

		window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			window.removeEventListener('beforeunload', preventLeaving);
		};
	}, []);

	useEffect(() => {
		gameInit();
	}, [
		props.indexed_images,
		stage?.minesweeper_grid,
		stage?.minesweeper_mines,
	]);

	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			is_playable.current = false;

			(success_game) ? successPlay() : failPlay();

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			unmountBoardAnimation(success_game, () => {
				setTimeout(() => {
					cb((success_game === true) ? success_game : 'ko');
				}, (delay_to_success * 1000));
			});
		},
	}), []);

	const nestArray = (row, col) => {
		let outerArray = [];
		for(let i = 0; i < row; i++) {
			const innerArray = [];
			for(let j = 0; j < col; j++) {
				innerArray.push({
					char:"",
					is_open:false,
					is_flagged:false,
				});
			}
			outerArray.push(innerArray);
		}

		return outerArray;
	};

	const floorRand = (scale) => {
		return Math.floor(Math.random() * scale);
	};

	const populateNestedArray = (nestedArray, count) => {
		while(count) {
			const y = floorRand(columns.current);
			const x = floorRand(columns.current);

			if(!isCellBombed(nestedArray[y][x])) {
				nestedArray[y][x].char = bomb_char_id;
				count--;
			}
		}

		return nestedArray;
	};

	const valsAdjacentCounts = (nestedArray) => {
		for(let i = 0; i < nestedArray.length; i++) {
			for(let j = 0; j < nestedArray[0].length; j++) {
				if(isCellBombed(nestedArray[i][j])) {
					nestedArray = addOneNestedArrAdjacents(nestedArray, i, j);
				}
			}
		}

		return nestedArray;
	};

	const addOneNestedArrAdjacents = (nestedArray, i, j) => {
		let iList = [i - 1, i, i + 1];
		let jList = [j - 1, j, j + 1];

		for(let a of iList) {
			if(nestedArray[a]) {
				for(let b of jList) {
					if(nestedArray[a][b]?.char !== undefined && !isCellBombed(nestedArray[a][b])) {
						if(typeof nestedArray[a][b].char !== 'number') nestedArray[a][b].char = 0;
						nestedArray[a][b].char++;
					}
				}
			}
		}

		return nestedArray;
	};

	const chargeAreas = () => {
		areas.current = [];

		for(let i = 0; i < columns.current; i++) {
			for(let j = 0; j < columns.current; j++) {
				if(map.current[i][j].char == '' && !isCellArea(`${i}_${j}`)) {
					areas.current.push(recursionArea(i, j));
				}
			}
		}
	};

	const recursionArea = (row, column, array = []) => {
		let colList = [column - 1, column, column + 1];
		let rowList = [row - 1, row, row + 1];
		let is_size;

		for(let x of rowList) {
			for(let y of colList) {
				if(x > -1 && x < columns.current && y > -1 && y < columns.current) {
					const this_id = `${x}_${y}`;

					if(array.length != 0 && array != undefined) {
						is_size = array.find((obj) => (obj == this_id));
						is_size = (is_size != undefined) ? true : false;
					} else {
						is_size = false;
					}

					if(isCellEmpty(map.current[x][y]) && !is_size) {
						array.push(this_id);
						array = recursionArea(x, y, array);
					} else if(!isCellBombed(map.current[x][y]) && !is_size) {
						array.push(this_id);
					}
				}
			}
		}

		return array;
	};

	const isCellArea = (id) => {
		if(areas.current.length == 0) return false;

		let is_area = false;
		areas.current.map((arr) => {
			if(arr.filter((obj) => (obj == id)).length > 0) is_area = true;
		});

		return is_area;
	};

	const isCellClicked = (cell_props) => {
		return (cell_props?.is_open) ? true : false;
	};

	const countTotalOpened = () => {
		let total_open = 0;
		map.current.map((rows, row) => {
			rows.map((foo, col) => {
				if(isCellOpened(map.current[row][col])) total_open++;
			})
		});
		return total_open;
	};

	const countTotalFlags = (ensure_bomb = false) => {
		let total_flags = 0;
		map.current.map((rows, row) => {
			rows.map((foo, col) => {
				if(isCellFlagged(map.current[row][col])) {
					if(!ensure_bomb || isCellBombed(map.current[row][col])) total_flags++;
				}
			})
		});
		return total_flags;
	};

	const pendingBombsToFlag = () => {
		return total_bombs - countTotalFlags(true);
	};

	const usedFlags = () => {
		return total_bombs - countTotalFlags() - 1;
	};

	const flagCell = (row, col, force_flag = false) => {
		if(ended.current || isCellClicked(map.current[row][col])) return false;

		const pending_bombs_to_flag = pendingBombsToFlag();
		const used_flags = usedFlags();

		if(isCellFlagged(map.current[row][col]) && !force_flag) {
			unflagCellPlay();
			map.current[row][col].is_flagged = false;
			doUnflagCell(row, col);
			doCloseCell(row, col);
	
			refs.current['layer'].draw();

			if(props.updatePendingFlags != undefined) props.updatePendingFlags(used_flags);
			return;
		}

		if(pending_bombs_to_flag == 0) return;

		flagCellPlay();
		map.current[row][col].is_flagged = true;
		doFlagCell(row, col);
		doOpenCell(row, col);

		refs.current['layer'].draw();

		isSolved();
		if(props.updatePendingFlags != undefined) props.updatePendingFlags(used_flags);
	};

	const isCellOpened = (cell_props) => {
		return (cell_props?.is_open) ? true : false;
	};

	const isCellFlagged = (cell_props) => {
		return (cell_props?.is_flagged) ? true : false;
	};

	const isCellBombed = (cell_props) => {
		return (cell_props?.char == bomb_char_id) ? true : false;
	};

	const isCellEmpty = (cell_props) => {
		return (cell_props?.char == clear_char_id) ? true : false;
	};

	const failGame = (row, col) => {
		ended.current = true;
		refs.current['tapper'].opacity(0.5);

		if(refs.current[`cell_bg_${row}_${col}`] != undefined) {
			refs.current[`cell_bg_${row}_${col}`].setAttrs({
				opacity:1,
			});
		}

		refs.current['layer'].draw();

		clickAndExplodeBombPlay();
		props.onEnded(false);
	};

	const isSolved = () => {
		let is_solved = false;

		if(countTotalOpened() == (total_cells - total_bombs) || pendingBombsToFlag() == 0) {
			is_solved = true;
			flagCellPlay();
			props.onEnded(is_solved);
		} else {
			flagCellPlay();
		}

		ended.current = is_solved;
	};

	const onFlagCell = (row, col, e) => {
		e.evt.preventDefault();
		flagCell(row, col, false);
		flag_event.current = true;
	};

	const onHack = (row, col) => {
		refs.current['hack'].setAttrs({ fill:(map.current[row][col].char == 'X') ? 'red' : 'black' });
		refs.current['layer'].draw();
	};

	const doOpenCell = (row, col) => {
		if(is_playable.current === false) return;

		refs.current[`bg_${row}_${col}`].setAttrs({
			opacity:0,
		});
		score.current += 10;
	};

	const doCloseCell = (row, col) => {
		if(is_playable.current === false) return;

		refs.current[`bg_${row}_${col}`].setAttrs({
			opacity:1,
		});
	};

	const doFlagCell = (row, col) => {
		if(is_playable.current === false) return;

		const flag_group = new Konva.Group({
			x:col * cell_size,
			y:row * cell_size,
			width:cell_size,
			height:cell_size,
		});

		const flag_bg = new Konva.Rect({
			width:cell_size,
			height:cell_size,
			fill:flag_bg_color,
		});

		let flag_img;
		if(flag_image) {
			flag_img = new Konva.Rect({
				width:flag_image.image_w * flag_image_scale,
				height:flag_image.image_h * flag_image_scale,
				offsetX:-(cell_size - flag_image.image_w * flag_image_scale) / 2,
				offsetY:-(cell_size - flag_image.image_h * flag_image_scale) / 2,
				fillPatternImage:flag_image.fillPatternImage,
				fillPatternRepeat:'no-repeat',
				fillPatternScaleX:flag_image_scale,
				fillPatternScaleY:flag_image_scale,
			});
		} else {
			flag_img = new Konva.Group({});

			const size_portion = 25;

			const flag_svg_shadow = new Konva.Path({
				fill:'gray',
				data:'M14.76,20.98c0,0.29-1.96,0.52-4.38,0.52S6,21.27,6,20.98c0-0.27,1.79-0.5,4.05-0.51c0.11,0,0.22,0,0.33,0C12.8,20.47,14.76,20.7,14.76,20.98z',
				width:size_portion,
				height:size_portion,
				pos_x:0,
				pos_y:0,
				x:size_portion * 0.06,
				y:size_portion * 0.06,
				stroke:'gray',
				strokeWidth:0.5,
				lineCap:'round',
				lineJoin:'round',
			});

			const flag_svg_mast = new Konva.Line({
				points:[8.76, 3.5, 10.38, 20.98],
				width:size_portion,
				height:size_portion,
				pos_x:0,
				pos_y:0,
				x:size_portion * 0.06,
				y:size_portion * 0.06,
				stroke:(is_flag_color_bright) ? 'black' : 'white',
				strokeWidth:1,
				lineCap:'round',
				lineJoin:'round',
			});

			const flag_svg_flag = new Konva.Path({
				fill:'red',
				data:'M8.85,4.53C9.9,5.38,10.61,6.6,10.74,8s-0.35,2.71-1.23,3.72L19,7.32L8.85,4.53z',
				width:size_portion,
				height:size_portion,
				pos_x:0,
				pos_y:0,
				x:size_portion * 0.06,
				y:size_portion * 0.06,
				stroke:'red',
				strokeWidth:0.5,
				lineCap:'round',
				lineJoin:'round',
			});

			flag_img.add(flag_svg_shadow);
			flag_img.add(flag_svg_mast);
			flag_img.add(flag_svg_flag);
		}

		flag_group.add(flag_bg);
		flag_group.add(flag_img);
		refs.current[`flag_${row}_${col}`] = flag_group;

		flag_group.cache(default_cache_props);

		refs.current['flags'].add(flag_group);

		refs.current['layer'].draw();
	};

	const doUnflagCell = (row, col) => {
		if(is_playable.current === false) return;

		refs.current[`flag_${row}_${col}`].destroy();

		refs.current['layer'].draw();
	};

	const onClickCell = (row, col) => {
		if(ended.current) return false;

		if(!flag_event.current && !isCellFlagged(map.current[row][col]) && !isCellClicked(map.current[row][col])) {
			map.current[row][col].is_open = true;
			doOpenCell(row, col);

			if(ended.current === false) {
				if(isCellEmpty(map.current[row][col])) {
					prepareZone(row, col);
					clickCellPlay();
				} else if(isCellBombed(map.current[row][col])) {
					score.current -= 10;
					failGame(row, col);
				}

				props.onScore(score.current);
			}

			if(countTotalOpened() == (total_cells - total_bombs) && !isCellBombed(map.current[row][col])) {
				forceFinishGame();
			}
		} else {
			flag_event.current = false;
		}

		refs.current['layer'].draw();
	};

	const forceFinishGame = () => {
		map.current.map((rows, row) => {
			rows.map((foo, col) => {
				if(isCellBombed(map.current[row][col])) {
					map.current[row][col].is_flagged = true;
				}
			});
		});

		isSolved();
	};

	const prepareZone = (row, col) => {
		let j = [];
		for(let i = 0; i < areas.current.length; i++) {
			for(let e = 0; e < areas.current[i].length; e++) {
				if(areas.current[i][e] == `${row}_${col}`) {
					j.push(i);
				}
			}
		}

		if(j.length != 0) {
			for(let i = 0; i < j.length; i++) {
				for(let e = 0; e < areas.current[j[i]].length; e++) {
					if(!isCellFlagged(areas.current[j[i]][e])) {
						const coords = areas.current[j[i]][e].split("_");
						map.current[coords[0]][coords[1]].is_open = true;

						doOpenCell(coords[0], coords[1]);
					}
				}
			}
		}
	};

	const gameInit = () => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((_size) => {
				size.current = _size;

				const nestedArray = populateNestedArray(nestArray(columns.current, columns.current), total_bombs);
				map.current = valsAdjacentCounts(nestedArray);

				chargeAreas(nestedArray);

				setFullLoaded(true);

				setTimeout(() => {
					if(refs.current['layer'] == undefined) return;
	
					if(is_playable.current === false) {
						mountBoardAnimation();
					}
				}, 50);
			});
		}

		if(props.setNumFlags != undefined) props.setNumFlags(total_bombs);
	};

	const handleButtonPress = (row, col, e) => {
		buttonPressTimer.current = setTimeout(() => {
			onFlagCell(row, col, e);
		}, 600);
	};

	const handleButtonRelease = () => {
		clearTimeout(buttonPressTimer.current);
	};

	const mountBoardAnimation = () => {
		if(is_mounting.current) return;

		if(is_builder && !is_test) {
			is_playable.current = true;

			props.startTimer();
			return;
		}

		is_mounting.current = true;

		if(refs.current['static_items'] != undefined) {
			refs.current['static_items'].cache(default_cache_props);
		}

		setTimeout(() => {
			let count = 0;

			[...Array(bombs_in_mount_animation).keys()].map((foo, b) => {
				const fake_bomb = refs.current[`init_bomb_${b}`];

				if(fake_bomb == undefined) return;

				refs.current[`init_bomb_${b}`].cache(default_cache_props);

				fake_bomb.setAttrs({
					scaleX:0,
					scaleY:0,
				});

				fake_bomb.to({
					x:circle_radius + cell_size / 2,
					scaleX:1,
					scaleY:1,
					duration:0.8,
					easing:Konva.Easings.StrongEaseOut,
					onFinish:() => {
						fake_bomb.to({
							x:size.current * Math.sqrt(2) + cell_size / 2,
							scaleX:1.2,
							scaleY:1.2,
							duration:0.8,
							easing:Konva.Easings.StrongEaseIn,
							onFinish:() => {
								count++;

								if(count < bombs_in_mount_animation) return;

								if(refs.current['mount_animation'] == undefined) return;

								refs.current['mount_animation'].setAttrs({
									x:size.current + 10,
									scaleX:0,
									scaleY:0,
									opacity:0,
								});

								refs.current['layer'].draw();

								if(refs.current['whole_tapper'] != undefined) {
									refs.current['whole_tapper'].setAttrs({
										y:size.current + 10,
									});
								}

								is_playable.current = true;
								is_mounting.current = false;

								props.startTimer();
							},
						});
					},
				});
			});
		}, 700);
	};

	const unmountBoardAnimation = (is_solved = true, cb) => {
		refs.current['whole_tapper'].setAttrs({
			y:0,
		});

		ended.current = true;
		refs.current['tapper'].opacity(0.5);
		cb();
	};

	let tapper_count_cell = 0;

	if(full_loaded === false) return null;

	const bomb_image = props.indexed_images.minesweeper_bomb_image;
	const bomb_image_scale = cell_size / Math.max((bomb_image?.image_w ?? 100), (bomb_image?.image_h ?? 100));

	const flag_image = props.indexed_images.minesweeper_flag_image;
	const flag_image_scale = cell_size / Math.max((flag_image?.image_w ?? 100), (flag_image?.image_h ?? 100));

	const main_classes = ['canvas_cont', 'not_bordered'];	// always not_bordered because it already has the lines in the grid

	return (
		<Stage
			className={main_classes.join(" ")}
			width={size.current}
			height={size.current}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
			>
				<Rect
					x={0}
					y={0}
					fill={board_bg_color}
					width={size.current}
					height={size.current}
				/>

				<Group
					ref={(node) => { refs.current['static_items'] = node; }}
				>
					{/* TEXT - ini */}
					{map.current.map((rows, row) => {
						return (
							rows.map((cell, col) => {
								if(isCellEmpty(cell) || isCellBombed(cell)) return;

								return (
									<Text
										key={`number_${row}_${col}`}
										x={col * cell_size}
										y={row * cell_size}
										width={cell_size}
										height={cell_size}
										fontSize={cell_size * 0.6}
										fontFamily='Helvetica'
										fill={text_color}
										wrap='char'
										align='center'
										verticalAlign='middle'
										text={cell.char}
									/>
								)
							})
						)
					})}
					{/* TEXT - end */}

					{/* BOMBS - ini */}
					{map.current.map((rows, row) => {
						return (
							rows.map((cell, col) => {
								if(!isCellBombed(cell)) return;

								return (
									<Group
										key={`bomb_${row}_${col}`}
										x={col * cell_size}
										y={row * cell_size}
										width={cell_size}
										height={cell_size}
									>
										<Rect
											ref={(node) => { refs.current[`cell_bg_${row}_${col}`] = node; }}
											width={cell_size}
											height={cell_size}
											fill={exploded_bomb_bg_color}
											opacity={1}
										/>

										{(!bomb_image) ? (
											<DefaultMineIcon
												theme={(is_bg_color_bright) ? 'light' : 'dark'}
												size_portion={cell_size}
												path_scale={cell_size * 0.85 * 0.04}
											/>
										) : (
											<Rect
												width={bomb_image.image_w * bomb_image_scale}
												height={bomb_image.image_h * bomb_image_scale}
												offsetX={-(cell_size - bomb_image.image_w * bomb_image_scale) / 2}
												offsetY={-(cell_size - bomb_image.image_h * bomb_image_scale) / 2}
												fillPatternImage={bomb_image.fillPatternImage}
												fillPatternRepeat='no-repeat'
												fillPatternScaleX={bomb_image_scale}
												fillPatternScaleY={bomb_image_scale}
											/>
										)}
									</Group>
								)
							})
						)
					})}
				</Group>
				{/* BOMBS - end */}

				{/* FLAGS - ini */}
				<Group ref={(node) => { refs.current['flags'] = node; }} />
				{/* FLAGS - end */}

				<Group
					ref={(node) => { refs.current['tapper'] = node; }}
				>
					{map.current.map((rows, row) => {
						return (
							rows.map((cell, col) => {
								const init_x = cell_size * Math.floor((tapper_count_cell) % columns.current);
								const init_y = cell_size * Math.floor((tapper_count_cell) / columns.current);
								tapper_count_cell += 1;

								return (
									<Rect
										key={`${row}_${col}_bg`}
										ref={(node) => { refs.current[`bg_${row}_${col}`] = node; }}
										id={`${row}_${col}`}
										x={init_x}
										y={init_y}
										width={cell_size}
										height={cell_size}
										fill={cell_bg_color}
										opacity={1}
										onTap={() => onClickCell(row, col)}
										onClick={() => onClickCell(row, col)}
										onMouseEnter={(hack_enabled) ? () => onHack(row, col) : null}
										onTouchStart={(e) => handleButtonPress(row, col, e)}
										onTouchEnd={handleButtonRelease}
										onContextMenu={(e) => onFlagCell(row, col, e)}
									/>
								)
							})
						)
					})}
				</Group>

				<Group
					ref={(node) => { refs.current['lines'] = node; }}
				>
					{map.current.map((foo, r) => {
						return (
							<Line
								key={`h_line_${r}`}
								strokeWidth={1}
								stroke={grid_bg_color}
								x={0}
								y={r * cell_size}
								points={[0, 0, size.current, 0]}
							/>
						)
					})}

					<Line
						strokeWidth={1}
						stroke={grid_bg_color}
						x={0}
						y={size.current}
						points={[0, 0, size.current, 0]}
					/>

					{map.current.map((foo, r) => {
						return (
							<Line
								key={`v_line_${r}`}
								strokeWidth={1}
								stroke={grid_bg_color}
								x={r * cell_size}
								y={0}
								points={[0, 0, 0, size.current]}
							/>
						)
					})}

					<Line
						strokeWidth={1}
						stroke={grid_bg_color}
						x={size.current}
						y={0}
						points={[0, 0, 0, size.current]}
					/>
				</Group>

				{(hack_enabled) && (
					<Rect
						ref={(node) => { refs.current['hack'] = node; }}
						x={(size.current - 4) / 2}
						y={(size.current - 4) / 2}
						width={4}
						height={4}
						fill='black'
						stroke='rgba(255, 255, 255, 0.5)'
						strokeWidth={1}
					/>
				)}

				{(!is_builder || is_test) && (
					<Group
						ref={(node) => { refs.current['mount_animation'] = node; }}
					>
						<Group
							x={size.current / 2}
							y={size.current / 2}
							offsetX={size.current / 2}
							offsetY={size.current / 2}
						>
							{[...Array(bombs_in_mount_animation).keys()].map((foo, b) => {
								const bomb_index = bombs_in_mount_animation - b - 1;

								return (
									<Group
										key={`bomb_${bomb_index}`}
										x={size.current / 2}
										y={size.current / 2}
										rotation={b * angle_between_bombs - 90}
										offsetX={cell_size / 2}
										offsetY={cell_size / 2}
									>
										<Group
											ref={(node) => { refs.current[`init_bomb_${bomb_index}`] = node; }}
											x={cell_size / 2}
											y={cell_size / 2}
											rotation={-b * angle_between_bombs + 90}
											scaleX={0}
											scaleY={0}
											offsetX={cell_size / 2}
											offsetY={cell_size / 2}
										>
											{(!bomb_image) ? (
												<DefaultMineIcon
													theme={(is_bg_color_bright) ? 'light' : 'dark'}
													size_portion={cell_size}
													path_scale={cell_size * 0.034}
												/>
											) : (
												<Rect
													width={bomb_image.image_w * bomb_image_scale}
													height={bomb_image.image_h * bomb_image_scale}
													offsetX={-(cell_size - bomb_image.image_w * bomb_image_scale) / 2}
													offsetY={-(cell_size - bomb_image.image_h * bomb_image_scale) / 2}
													fillPatternImage={bomb_image.fillPatternImage}
													fillPatternRepeat='no-repeat'
													fillPatternScaleX={bomb_image_scale}
													fillPatternScaleY={bomb_image_scale}
												/>
											)}
										</Group>
									</Group>
								)
							})}
						</Group>
					</Group>
				)}

				<Group
					ref={(node) => { refs.current['whole_tapper'] = node; }}
					y={0}
				>
					<Rect
						width={size.current}
						height={size.current}
						fill='white'
						opacity={0}
					/>
				</Group>
			</Layer>
		</Stage>
	)
});

export default MineSweeperCanvas;